"use client";
import React from "react";
import Link from "components/common/Link";
import Image from "components/common/Image";
import { get } from "utils/lodash";
import { formatPrice } from "utils/currencyFormatter";
import { useT } from "providers/I18nProvider";
import {
  shopCover,
  shopAvatar,
  shortAddress,
  roundRating,
} from "./cardFormat";

/**
 * Card địa điểm nổi bật: ảnh gallery làm nền, logo tròn đè góc dưới,
 * rating pill góc phải, badge trạng thái góc trái, giá từ.
 */
const ShopFeatureCard: React.FC<{ shop: any; className?: string }> = ({
  shop,
  className = "",
}) => {
  const t = useT();
  const cover = shopCover(shop);
  const avatar = shopAvatar(shop);
  const rating = roundRating(get(shop, "rating"));
  const minPrice = Number(get(shop, "minPrice")) || 0;
  const category = get(shop, "mainShopCategory.name") || "";
  const hasOffer = !!get(shop, "haveSlotDiscount");
  const badge = hasOffer
    ? { label: t("home.offerBadge"), cls: "bg-orange-500" }
    : get(shop, "allowBooking")
      ? { label: t("home.bookBadge"), cls: "bg-primary-ink" }
      : null;

  return (
    <Link
      href={`/${get(shop, "slug")}`}
      className={`group flex flex-col rounded-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary overflow-hidden bg-white border border-gray-100 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-primary/40 ${className}`}
    >
      <div className="relative">
        <Image
          src={cover}
          alt={get(shop, "name") || ""}
          wrapClassName="w-full aspect-[4/3] bg-gray-100 overflow-hidden"
          className="transition-transform duration-300 group-hover:scale-[1.04]"
          sizes="(min-width: 768px) 300px, 70vw"
        />
        {badge && (
          <span
            className={`absolute left-3 top-3 rounded-full ${badge.cls} px-2 py-0.5 text-[12px] font-bold text-white shadow`}
          >
            {badge.label}
          </span>
        )}
        {rating && (
          <span className="absolute right-3 top-3 rounded-full bg-white px-2 py-0.5 text-xs font-bold text-primary shadow">
            {rating}
          </span>
        )}
        {/* Logo nằm gọn trong ảnh (trước đây lòi xuống thân card → mất ~1 hàng trống trên mobile) */}
        {/* Bọc div absolute: Image luôn tự thêm "relative" vào wrapClassName nên đặt absolute trực tiếp không ăn */}
        {avatar && (
          <div className="absolute bottom-2 left-2 md:bottom-3 md:left-3">
            <Image
              src={avatar}
              alt=""
              wrapClassName="w-8 h-8 md:w-10 md:h-10 rounded-full ring-2 ring-white overflow-hidden bg-white shadow"
              sizes="40px"
            />
          </div>
        )}
      </div>
      <div className="p-2.5 md:p-3 flex flex-col gap-1 flex-1">
        {/* Tên 2 dòng cố định (khoá chiều cao để các card thẳng hàng) — trước bị cắt còn "Tiệm làm đẹp C…" */}
        <h3 className="font-bold text-black01 text-[14px] md:text-[15px] leading-[1.3] line-clamp-2 [display:-webkit-box] h-[2.6em] overflow-hidden !mb-0">
          {get(shop, "name")}
        </h3>
        <p className="text-xs text-gray-500 truncate !mb-0">
          📍 {shortAddress(get(shop, "displayedAddress"))}
        </p>
        {/* Giá tách dòng riêng: thông tin quyết định không được bị "…" nuốt mất.
            Mobile (thẻ ~170px) bỏ danh mục — chỉ còn "Massag…" đọc không ra, giá đứng đầu dòng. */}
        <div className="mt-auto pt-1 flex items-baseline md:justify-between gap-2 min-w-0">
          {category ? (
            <span className="hidden md:inline text-[12px] text-gray-600 truncate">{category}</span>
          ) : null}
          {minPrice > 0 ? (
            <span className="shrink-0 text-[13px] md:text-sm font-extrabold text-primary-ink whitespace-nowrap">
              {t("home.fromPrice", { price: formatPrice(minPrice) })}
            </span>
          ) : null}
        </div>
      </div>
    </Link>
  );
};

export default ShopFeatureCard;
